import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { getAudioAnnouncementSendHistory } from '../../services/AudioAnnouncementService';
import type { SendHistoryResponse } from '../../types/AudioAnnouncement';

const DEFAULT_LIMIT = 20;
const DEFAULT_SORT_ORDER = 'DESC';
const POLL_INTERVAL_MS = 3000;

function useGetAudioAnnouncementSendHistory(
  fleetId: string | null,
  page: number,
  limit: number = DEFAULT_LIMIT,
  sortOrder: string = DEFAULT_SORT_ORDER,
): UseQueryResult<SendHistoryResponse> & { prevPage: boolean; nextPage: boolean; totalPages: number | null } {
  const skip = Math.max(0, (page - 1) * limit);
  const enabled = Boolean(fleetId);

  const query = useQuery(
    ['audio-announcement-send-history', fleetId ?? '', page, limit, sortOrder],
    (): Promise<SendHistoryResponse> => getAudioAnnouncementSendHistory(fleetId as string, { limit, skip, sortOrder }),
    {
      retry: 1,
      enabled,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      refetchInterval: enabled ? POLL_INTERVAL_MS : false,
      refetchIntervalInBackground: false,
    },
  );

  const results = query.data?.results ?? [];
  const total = query.data?.total;
  const totalPages = total != null && limit > 0 ? Math.ceil(total / limit) : null;
  const prevPage = page > 1;
  const nextPage = total != null ? page * limit < total : results.length >= limit;

  return {
    ...query,
    prevPage,
    nextPage,
    totalPages,
  };
}

export default useGetAudioAnnouncementSendHistory;
